import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  UncontrolledTooltip,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DismissiblePageTip from "../../../Components/Common/DismissiblePageTip";
import PageHelpLink from "../../../Components/Common/PageHelpLink";
import { helpUrlForRoute } from "../../../constants/helpRouteMap";
import {
  apiErrorMessage,
  connectVtex,
  disconnectVtex,
  getVtexIntegration,
  syncVtexContacts,
  TenantIntegration,
  testVtexConnection,
  updateVtex,
} from "../../../helpers/integrations_helper";
import {
  formatInTenantTimezone,
  readDisplayTimezone,
} from "../../../helpers/tenantTimezone";

/** Duração legível do sync VTEX — segundos ou min+seg. */
function formatSyncDuration(
  seconds: number,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  if (seconds < 60) {
    return t("integrations.vtex.syncDurationSeconds", { seconds });
  }
  const minutes = Math.floor(seconds / 60);
  const rem = seconds % 60;
  if (rem === 0) {
    return t("integrations.vtex.syncDurationMinutes", { minutes });
  }
  return t("integrations.vtex.syncDurationHuman", { minutes, seconds: rem });
}

type FormValues = {
  account_name: string;
  app_key: string;
  app_token: string;
};

const MASTER_DATA_JSON = `{
  "email": "{!email}",
  "firstName": "{!firstName}",
  "lastName": "{!lastName}",
  "rclastcart": "{!rclastcart}",
  "rclastcartvalue": "{!rclastcartvalue}",
  "isNewsletterOptIn": "{!isNewsletterOptIn}",
  "rclastsession": "{!rclastsession}"
}`;

function checklistKey(publicKey: string, part: string): string {
  return `brenvio:vtex-checklist:${publicKey}:${part}`;
}

/**
 * Conectar / gerenciar VTEX — checklist + wizard Master Data (DOC 11 § 6.1).
 * Por quê: Carla precisa copiar URL/JSON/secret sem adivinhar o Admin VTEX.
 */
const VtexIntegrationPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.language || "pt-BR";

  document.title = t("integrations.vtex.documentTitle");

  const helpHref = helpUrlForRoute("/integrations/vtex", locale);
  const tz = readDisplayTimezone();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [row, setRow] = useState<TenantIntegration | null>(null);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [testOk, setTestOk] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [secretSaved, setSecretSaved] = useState(false);
  const [jsonCopied, setJsonCopied] = useState(false);
  const [forceFullSync, setForceFullSync] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());

  // Cooldown espelha backend — force_full NÃO ignora intervalo (TenantIntegrationService).
  const cooldownMinutes = row?.vtex_sync_cooldown_minutes ?? 15;

  const cooldownEndMs = useMemo(() => {
    if (!row?.last_synced_at) return null;
    const syncedAt = new Date(row.last_synced_at).getTime();
    if (Number.isNaN(syncedAt)) return null;
    return syncedAt + cooldownMinutes * 60 * 1000;
  }, [row?.last_synced_at, cooldownMinutes]);

  const cooldownRemainingMs =
    cooldownEndMs != null ? Math.max(0, cooldownEndMs - nowMs) : 0;
  const inSyncCooldown = cooldownRemainingMs > 0;

  useEffect(() => {
    if (cooldownEndMs == null) return;
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [cooldownEndMs]);

  const syncCountdownLabel = useMemo(() => {
    if (!inSyncCooldown) return null;
    const totalSec = Math.ceil(cooldownRemainingMs / 1000);
    const minutes = Math.floor(totalSec / 60);
    const seconds = totalSec % 60;
    return t("integrations.vtex.syncCountdown", { minutes, seconds });
  }, [inSyncCooldown, cooldownRemainingMs, t]);

  const syncButtonDisabled = syncing || saving || inSyncCooldown;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getVtexIntegration();
      if (res?.success) {
        setRow(res.data);
        const pk = res.data.public_key;
        setUrlCopied(
          localStorage.getItem(checklistKey(pk, "url")) === "1"
        );
        setSecretSaved(
          localStorage.getItem(checklistKey(pk, "secret")) === "1"
        );
        setJsonCopied(
          localStorage.getItem(checklistKey(pk, "json")) === "1"
        );
        setTestOk(localStorage.getItem(checklistKey(pk, "test")) === "1");
      } else {
        setRow(null);
      }
    } catch (e) {
      const code = (e as { response?: { data?: { error?: { code?: string } } } })
        ?.response?.data?.error?.code;
      if (code === "INTEGRATION_NOT_CONFIGURED") {
        setRow(null);
      } else {
        setError(apiErrorMessage(e, t("integrations.error.load")));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const markChecklist = (part: string) => {
    if (!row?.public_key) return;
    localStorage.setItem(checklistKey(row.public_key, part), "1");
  };

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      account_name: row?.account_name || "",
      app_key: "",
      app_token: "",
    },
    validationSchema: Yup.object({
      account_name: Yup.string().required(t("integrations.vtex.required")),
      app_key: row
        ? Yup.string()
        : Yup.string().required(t("integrations.vtex.required")),
      app_token: row
        ? Yup.string()
        : Yup.string().required(t("integrations.vtex.required")),
    }),
    onSubmit: async (values) => {
      setSaving(true);
      setError(null);
      try {
        if (!row) {
          const res = await connectVtex({
            account_name: values.account_name.trim(),
            app_key: values.app_key.trim(),
            app_token: values.app_token.trim(),
          });
          setRow(res.data);
          if (res.data.webhook_secret) {
            setRevealedSecret(res.data.webhook_secret);
          }
          setUrlCopied(false);
          setSecretSaved(false);
          setJsonCopied(false);
          setTestOk(false);
          toast.success(res.message || t("integrations.vtex.connectedToast"));
          formik.resetForm();
        } else {
          const payload: {
            account_name: string;
            app_key?: string;
            app_token?: string;
          } = {
            account_name: values.account_name.trim(),
          };
          if (values.app_key.trim()) payload.app_key = values.app_key.trim();
          if (values.app_token.trim()) {
            payload.app_token = values.app_token.trim();
          }
          const res = await updateVtex(payload);
          setRow(res.data);
          toast.success(res.message || t("integrations.vtex.updatedToast"));
          formik.setFieldValue("app_key", "");
          formik.setFieldValue("app_token", "");
        }
      } catch (e) {
        setError(apiErrorMessage(e, t("integrations.error.save")));
      } finally {
        setSaving(false);
      }
    },
  });

  const onTest = async () => {
    setTesting(true);
    try {
      const res = await testVtexConnection();
      setTestOk(true);
      markChecklist("test");
      toast.success(res.data?.message || t("integrations.vtex.testOk"));
    } catch (e) {
      setTestOk(false);
      toast.error(apiErrorMessage(e, t("integrations.vtex.testFail")));
    } finally {
      setTesting(false);
    }
  };

  // Sync Master Data → Contatos: enfileira e faz poll até last_synced_at mudar (feedback leigo).
  const onSync = async () => {
    setSyncing(true);
    const previousSynced = row?.last_synced_at ?? null;
    try {
      await syncVtexContacts(
        forceFullSync ? { force_full: true } : undefined
      );
      toast.info(t("integrations.vtex.syncQueued"));

      let finished: TenantIntegration | null = null;
      for (let i = 0; i < 45; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const res = await getVtexIntegration();
        if (!res?.success) continue;
        setRow(res.data);
        if (
          res.data.last_sync?.progress_percent != null &&
          res.data.last_sync.progress_percent < 100
        ) {
          // Poll mostra % enquanto o Job ainda grava last_sync (LAC-126).
        }
        const next = res.data.last_synced_at;
        if (next && next !== previousSynced) {
          finished = res.data;
          break;
        }
        if (res.data.last_error) {
          toast.error(res.data.last_error);
          return;
        }
      }

      if (finished?.last_sync) {
        const s = finished.last_sync;
        toast.success(
          t("integrations.vtex.syncDone", {
            imported: s.imported,
            updated: s.updated,
            skipped: s.skipped,
          })
        );
        // Pedido fechado cancelou e-mail(s) de abandono ainda na fila.
        if ((s.emails_cancelled ?? 0) > 0) {
          toast.info(
            t("integrations.vtex.emailsCancelledToast", {
              count: s.emails_cancelled,
            })
          );
        }
        const touched = (s.imported ?? 0) + (s.updated ?? 0);
        if (
          touched === 0 &&
          (finished.vtex_customer_count ?? 0) === 0
        ) {
          toast.warn(t("integrations.vtex.syncEmptyToast"));
        }
      } else if (finished) {
        toast.success(t("integrations.vtex.syncDoneSimple"));
      } else {
        toast.info(t("integrations.vtex.syncStillRunning"));
      }
    } catch (e) {
      const code = (e as { response?: { data?: { error?: { code?: string } } } })
        ?.response?.data?.error?.code;
      if (code === "INTEGRATION_SYNC_COOLDOWN") {
        toast.warn(apiErrorMessage(e, t("integrations.vtex.syncCooldown")));
      } else {
        toast.error(apiErrorMessage(e, t("integrations.vtex.syncFail")));
      }
    } finally {
      setSyncing(false);
    }
  };

  const onRotate = async () => {
    setSaving(true);
    try {
      const res = await updateVtex({ rotate_webhook_secret: true });
      setRow(res.data);
      if (res.data.webhook_secret) {
        setRevealedSecret(res.data.webhook_secret);
      }
      setSecretSaved(false);
      if (res.data.public_key) {
        localStorage.removeItem(checklistKey(res.data.public_key, "secret"));
      }
      toast.success(t("integrations.vtex.secretRotatedUpdateTrigger"));
    } catch (e) {
      toast.error(apiErrorMessage(e, t("integrations.error.save")));
    } finally {
      setSaving(false);
    }
  };

  const onDisconnect = async () => {
    setSaving(true);
    try {
      await disconnectVtex();
      toast.success(t("integrations.vtex.disconnectedToast"));
      setConfirmDisconnect(false);
      navigate("/integrations");
    } catch (e) {
      toast.error(apiErrorMessage(e, t("integrations.error.save")));
    } finally {
      setSaving(false);
    }
  };

  const copyText = async (text: string, okKey: string, part?: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t(okKey));
      if (part === "url") {
        setUrlCopied(true);
        markChecklist("url");
      }
      if (part === "secret") {
        setSecretSaved(true);
        markChecklist("secret");
      }
      if (part === "json") {
        setJsonCopied(true);
        markChecklist("json");
      }
    } catch {
      toast.error(t("integrations.copyFail"));
    }
  };

  const headerSample = useMemo(() => {
    const secret =
      revealedSecret || t("integrations.vtex.secretPlaceholder");
    return `Content-Type: application/json
Accept: application/json
X-Brenvio-Vtex-Secret: ${secret}`;
  }, [revealedSecret, t]);

  const checklistItems = row
    ? [
        {
          id: "creds",
          done: true,
          label: t("integrations.vtex.check.creds"),
        },
        {
          id: "test",
          done: testOk,
          label: t("integrations.vtex.check.test"),
        },
        {
          id: "secret",
          done: secretSaved,
          label: t("integrations.vtex.check.secret"),
        },
        {
          id: "url",
          done: urlCopied,
          label: t("integrations.vtex.check.url"),
        },
        {
          id: "json",
          done: jsonCopied,
          label: t("integrations.vtex.check.json"),
        },
      ]
    : [];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("integrations.vtex.title")}
            pageTitle={t("integrations.title")}
          />
          <div className="d-flex justify-content-between align-items-start mb-3 gap-2 flex-wrap">
            <DismissiblePageTip tipId="integrations-vtex">
              {t("integrations.vtex.tip")}
            </DismissiblePageTip>
            <PageHelpLink href={helpHref} label={t("integrations.help")} />
          </div>

          {row && !row.last_webhook_at && (
            <Alert color="info" className="mb-3">
              {t("integrations.vtex.waitingWebhook")}
            </Alert>
          )}

          {row?.last_webhook_at && (
            <Alert color="success" className="mb-3">
              {t("integrations.vtex.lastWebhook", {
                when: formatInTenantTimezone(
                  row.last_webhook_at,
                  tz,
                  i18n.language
                ),
              })}
              {(row.events_today ?? 0) > 0 && (
                <div className="mt-1">
                  {t("integrations.vtex.eventsToday", {
                    count: row.events_today,
                  })}
                </div>
              )}
              {row.abandoned_cart_tag_id != null && (
                <div className="mt-2">
                  <Link
                    to={`/contacts?tag_ids=${row.abandoned_cart_tag_id}`}
                    className="alert-link"
                  >
                    {t("integrations.vtex.viewContacts")}
                  </Link>
                </div>
              )}
            </Alert>
          )}

          {syncing && (
            <Alert color="info" className="mb-3">
              {t("integrations.vtex.syncProgress", {
                percent: row?.last_sync?.progress_percent ?? 0,
              })}
            </Alert>
          )}

          {row?.last_synced_at && (
            <Alert color="light" className="mb-3 border">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                <span>
                  {t("integrations.vtex.lastSynced", {
                    when: formatInTenantTimezone(
                      row.last_synced_at,
                      tz,
                      i18n.language
                    ),
                  })}
                </span>
                {row.last_sync?.mode && (
                  <Badge
                    color={
                      row.last_sync.mode === "incremental"
                        ? "info"
                        : "secondary"
                    }
                    className="text-uppercase"
                  >
                    {row.last_sync.mode === "incremental"
                      ? t("integrations.vtex.syncModeIncremental")
                      : t("integrations.vtex.syncModeFull")}
                  </Badge>
                )}
                {row.last_sync?.duration_seconds != null &&
                  row.last_sync.duration_seconds >= 0 && (
                    <Badge color="light" className="text-muted border">
                      {formatSyncDuration(row.last_sync.duration_seconds, t)}
                    </Badge>
                  )}
              </div>
              {row.last_sync && (
                <div className="mt-1">
                  {t("integrations.vtex.lastSyncStats", {
                    imported: row.last_sync.imported,
                    updated: row.last_sync.updated,
                    skipped: row.last_sync.skipped,
                  })}
                </div>
              )}
              {(row.last_sync?.emails_cancelled ?? 0) > 0 && (
                <div className="mt-1 text-muted">
                  {t("integrations.vtex.emailsCancelledToast", {
                    count: row.last_sync!.emails_cancelled,
                  })}
                </div>
              )}
              {(row.vtex_customer_count ?? 0) > 0 && (
                <div className="mt-1">
                  {t("integrations.vtex.customerCount", {
                    count: row.vtex_customer_count,
                  })}
                </div>
              )}
              {syncCountdownLabel && (
                <div className="mt-1 text-muted small">{syncCountdownLabel}</div>
              )}
              {row.vtex_customer_tag_id != null && (
                <div className="mt-2">
                  <Link
                    to={`/contacts?tag_ids=${row.vtex_customer_tag_id}`}
                    className="alert-link"
                  >
                    {t("integrations.vtex.viewCustomers")}
                  </Link>
                </div>
              )}
            </Alert>
          )}

          {row && (row.vtex_customer_count ?? 0) === 0 && !row.last_synced_at && (
            <Alert color="secondary" className="mb-3">
              {t("integrations.vtex.syncHint")}
            </Alert>
          )}

          {row?.last_synced_at && (row.vtex_customer_count ?? 0) === 0 && (
            <Alert color="warning" className="mb-3">
              {t("integrations.vtex.syncEmptyState")}{" "}
              {helpHref ? (
                <a
                  href={`${helpHref}#newsletter-opt-in`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="alert-link"
                >
                  {t("integrations.vtex.syncEmptyHelpLink")}
                </a>
              ) : null}
            </Alert>
          )}

          {row && row.automation_enabled === false && (
            <Alert color="warning" className="mb-3">
              {t("integrations.vtex.automationOff")}{" "}
              <Link to="/automations/abandoned-cart" className="alert-link">
                {t("integrations.vtex.automationLink")}
              </Link>
            </Alert>
          )}

          {row?.last_skip_reason && (
            <Alert color="secondary" className="mb-3">
              {t(`integrations.vtex.skip.${row.last_skip_reason}`, {
                defaultValue: t("integrations.vtex.skip.generic"),
              })}
            </Alert>
          )}

          <p className="mb-3">
            <Link to="/integrations">{t("integrations.back")}</Link>
          </p>

          {error && (
            <Alert color="danger" className="mb-3">
              {error}
            </Alert>
          )}

          {revealedSecret && (
            <Alert color="warning" className="mb-3">
              <strong>{t("integrations.vtex.secretOnce")}</strong>
              <div className="mt-2 font-monospace small text-break">
                {revealedSecret}
              </div>
              <Button
                color="warning"
                outline
                size="sm"
                className="mt-2"
                type="button"
                onClick={() =>
                  void copyText(
                    revealedSecret,
                    "integrations.vtex.secretCopied",
                    "secret"
                  )
                }
              >
                {t("integrations.copy")}
              </Button>
            </Alert>
          )}

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <Row>
              <Col lg={7}>
                <Card>
                  <CardHeader>
                    <h5 className="card-title mb-0">
                      {row
                        ? t("integrations.vtex.credentialsTitle")
                        : t("integrations.vtex.connectTitle")}
                    </h5>
                  </CardHeader>
                  <CardBody>
                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();
                        formik.handleSubmit();
                      }}
                    >
                      <div className="mb-3">
                        <Label htmlFor="account_name">
                          {t("integrations.vtex.accountName")}
                        </Label>
                        <Input
                          id="account_name"
                          name="account_name"
                          value={formik.values.account_name}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            !!(
                              formik.touched.account_name &&
                              formik.errors.account_name
                            )
                          }
                          placeholder="minhaloja"
                        />
                        <FormFeedback>
                          {formik.errors.account_name}
                        </FormFeedback>
                        <div className="form-text">
                          {t("integrations.vtex.accountHint")}
                        </div>
                      </div>
                      <div className="mb-3">
                        <Label htmlFor="app_key">
                          {t("integrations.vtex.appKey")}
                          {row && row.app_key_masked
                            ? ` (${row.app_key_masked})`
                            : ""}
                        </Label>
                        <Input
                          id="app_key"
                          name="app_key"
                          value={formik.values.app_key}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            !!(formik.touched.app_key && formik.errors.app_key)
                          }
                          placeholder={
                            row
                              ? t("integrations.vtex.leaveBlank")
                              : undefined
                          }
                        />
                        <FormFeedback>{formik.errors.app_key}</FormFeedback>
                      </div>
                      <div className="mb-3">
                        <Label htmlFor="app_token">
                          {t("integrations.vtex.appToken")}
                        </Label>
                        <Input
                          id="app_token"
                          name="app_token"
                          type="password"
                          autoComplete="new-password"
                          value={formik.values.app_token}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            !!(
                              formik.touched.app_token &&
                              formik.errors.app_token
                            )
                          }
                          placeholder={
                            row
                              ? t("integrations.vtex.leaveBlank")
                              : undefined
                          }
                        />
                        <FormFeedback>{formik.errors.app_token}</FormFeedback>
                      </div>
                      {row && (
                        <div className="mb-3 form-check">
                          <Input
                            id="force_full_sync"
                            name="force_full_sync"
                            type="checkbox"
                            className="form-check-input"
                            checked={forceFullSync}
                            onChange={(e) => setForceFullSync(e.target.checked)}
                            disabled={syncing || saving}
                          />
                          <Label
                            htmlFor="force_full_sync"
                            className="form-check-label"
                          >
                            {t("integrations.vtex.syncFull")}
                          </Label>
                          <div className="form-text">
                            {t("integrations.vtex.syncFullHint")}
                          </div>
                        </div>
                      )}
                      <div className="d-flex flex-wrap gap-2">
                        <Button color="primary" type="submit" disabled={saving}>
                          {saving && <Spinner size="sm" className="me-1" />}
                          {row
                            ? t("integrations.vtex.save")
                            : t("integrations.connect")}
                        </Button>
                        {row && (
                          <>
                            <Button
                              color="secondary"
                              outline
                              type="button"
                              disabled={testing || saving}
                              onClick={() => void onTest()}
                            >
                              {testing && (
                                <Spinner size="sm" className="me-1" />
                              )}
                              {t("integrations.vtex.test")}
                            </Button>
                            <Button
                              id="vtex-sync-btn"
                              color="secondary"
                              outline
                              type="button"
                              disabled={syncButtonDisabled}
                              onClick={() => void onSync()}
                            >
                              {syncing && (
                                <Spinner size="sm" className="me-1" />
                              )}
                              {t("integrations.vtex.sync")}
                            </Button>
                            {inSyncCooldown && (
                              <UncontrolledTooltip
                                placement="top"
                                target="vtex-sync-btn"
                              >
                                {t("integrations.vtex.syncCooldownTooltip", {
                                  minutes: cooldownMinutes,
                                })}
                              </UncontrolledTooltip>
                            )}
                            <Button
                              color="danger"
                              outline
                              type="button"
                              disabled={saving}
                              onClick={() => setConfirmDisconnect(true)}
                            >
                              {t("integrations.vtex.disconnect")}
                            </Button>
                          </>
                        )}
                      </div>
                    </Form>
                  </CardBody>
                </Card>

                {row && (
                  <Card className="mt-3">
                    <CardHeader>
                      <h5 className="card-title mb-0">
                        {t("integrations.vtex.wizardTitle")}
                      </h5>
                    </CardHeader>
                    <CardBody>
                      <ol className="ps-3 mb-3">
                        <li className="mb-2">
                          {t("integrations.vtex.wizard.step1")}
                        </li>
                        <li className="mb-2">
                          {t("integrations.vtex.wizard.step2")}
                        </li>
                        <li className="mb-2">
                          {t("integrations.vtex.wizard.step3")}
                        </li>
                        <li className="mb-2">
                          {t("integrations.vtex.wizard.step4")}
                        </li>
                        <li className="mb-0">
                          {t("integrations.vtex.wizard.step5")}
                        </li>
                      </ol>

                      <Label>{t("integrations.vtex.webhookUrl")}</Label>
                      <div className="d-flex gap-2 mb-3">
                        <Input
                          readOnly
                          value={row.webhook_url}
                          className="font-monospace small"
                        />
                        <Button
                          color="light"
                          type="button"
                          onClick={() =>
                            void copyText(
                              row.webhook_url,
                              "integrations.vtex.urlCopied",
                              "url"
                            )
                          }
                        >
                          {t("integrations.copy")}
                        </Button>
                      </div>

                      <Label>{t("integrations.vtex.headersLabel")}</Label>
                      <pre className="bg-light border rounded p-2 small mb-2 text-break">
                        {headerSample}
                      </pre>
                      <Button
                        color="light"
                        size="sm"
                        type="button"
                        className="mb-3"
                        onClick={() =>
                          void copyText(
                            headerSample,
                            "integrations.vtex.headersCopied"
                          )
                        }
                      >
                        {t("integrations.vtex.copyHeaders")}
                      </Button>

                      <Label>{t("integrations.vtex.jsonLabel")}</Label>
                      <pre className="bg-light border rounded p-2 small mb-2 text-break">
                        {MASTER_DATA_JSON}
                      </pre>
                      <div className="d-flex flex-wrap gap-2">
                        <Button
                          color="primary"
                          outline
                          size="sm"
                          type="button"
                          onClick={() =>
                            void copyText(
                              MASTER_DATA_JSON,
                              "integrations.vtex.jsonCopied",
                              "json"
                            )
                          }
                        >
                          {t("integrations.vtex.copyJson")}
                        </Button>
                        <Button
                          color="warning"
                          outline
                          size="sm"
                          type="button"
                          disabled={saving}
                          onClick={() => void onRotate()}
                        >
                          {t("integrations.vtex.rotateSecret")}
                        </Button>
                        <a
                          className="btn btn-link btn-sm"
                          href={helpHref}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t("integrations.vtex.openHelp")}
                        </a>
                      </div>
                      <p className="text-muted small mt-3 mb-0">
                        {t("integrations.vtex.wizardNote")}
                      </p>
                      {row.last_error && (
                        <Alert color="danger" className="mt-3 mb-0">
                          {row.last_error}
                        </Alert>
                      )}
                    </CardBody>
                  </Card>
                )}
              </Col>

              {row && (
                <Col lg={5}>
                  <Card>
                    <CardHeader className="d-flex justify-content-between align-items-center">
                      <h5 className="card-title mb-0">
                        {t("integrations.vtex.checklistTitle")}
                      </h5>
                      <Badge color="success">
                        {t("integrations.connected")}
                      </Badge>
                    </CardHeader>
                    <CardBody>
                      <p className="text-muted small">
                        {t("integrations.vtex.checklistTip")}
                      </p>
                      <ListGroup flush>
                        {checklistItems.map((item) => (
                          <ListGroupItem
                            key={item.id}
                            className="px-0 d-flex align-items-start gap-2"
                          >
                            <i
                              className={
                                item.done
                                  ? "ri-checkbox-circle-fill text-success"
                                  : "ri-checkbox-blank-circle-line text-muted"
                              }
                              aria-hidden
                            />
                            <span
                              className={
                                item.done ? "" : "text-muted"
                              }
                            >
                              {item.label}
                            </span>
                          </ListGroupItem>
                        ))}
                      </ListGroup>
                      <Alert color="info" className="mt-3 mb-0 small">
                        {t("integrations.vtex.nextFatias")}
                      </Alert>
                    </CardBody>
                  </Card>
                </Col>
              )}
            </Row>
          )}
        </Container>
      </div>

      <Modal
        isOpen={confirmDisconnect}
        toggle={() => setConfirmDisconnect(false)}
      >
        <ModalHeader toggle={() => setConfirmDisconnect(false)}>
          {t("integrations.vtex.disconnect")}
        </ModalHeader>
        <ModalBody>{t("integrations.vtex.disconnectConfirm")}</ModalBody>
        <ModalFooter>
          <Button
            color="light"
            onClick={() => setConfirmDisconnect(false)}
            disabled={saving}
          >
            {t("integrations.cancel")}
          </Button>
          <Button
            color="danger"
            onClick={() => void onDisconnect()}
            disabled={saving}
          >
            {saving && <Spinner size="sm" className="me-1" />}
            {t("integrations.vtex.disconnect")}
          </Button>
        </ModalFooter>
      </Modal>

      <ToastContainer />
    </React.Fragment>
  );
};

export default VtexIntegrationPage;
